import { ChangeDetectorRef, Component, Input, forwardRef } from '@angular/core';
import { GenericDeviceValidator, getDeviceIconIdentifier, isNotNullOrUndefined } from 'ngx-core-services';
import { FormGroup, FormArray, FormControl, AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  ApplyPipeModule,
  CardAccordionComponent,
  CustomerItemComponent,
  SimpleButtonComponent,
  SimpleInputComponent,
  TitleContentComponent,
  ToastFacade,
} from 'ngx-zcs-components';
import { map, tap } from 'rxjs/operators';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { combineLatest, defer } from 'rxjs';

import { getInverterSerialErrorMessage } from '../create-firebase/create.utils';
import { DeviceSerialValidatorPipe } from '../pipes/errors.pipe';
import { selectedSystem, SystemsState } from '../../store';

export type RemoveInverterFromPlant = 'Uninstall' | 'Delete';

type InverterForm = FormGroup<{
  inverterSerial: FormControl<string | null>;
  added: FormControl<boolean | null>;
  dismiss: FormControl<RemoveInverterFromPlant | null>;
}>;

export type InverterValidators = {
  syncValidators?: ValidatorFn[];
  asyncValidators?: AsyncValidatorFn[];
};

export type VirtualDevicesForm = FormGroup<{
  inverterList: FormArray<InverterForm>;
}>;

const labels = {
  active: 'Active',
  addInverter: 'Add',
  atLeastOneActiveInverter: 'At least one active inverter must be present',
  attention: 'Attention',
  delete: 'Delete',
  deviceAlreadyOnACertifiedPlant: 'Device already on a certified plant',
  deviceNotExist: 'Device does not exist',
  inverterListDescription: 'Enter the list of your devices specifying the serial numbers. You can find the device serial number on the adhesive label. For example, for inverters, it is located on the side.',
  inverterListPlaceholder: 'E.g. ZM2',
  inverterListTitle: 'Inverter List',
  serialNotValid: 'Serial not valid',
  thereAreUnsavedChanges: 'There are unsaved changes',
  uninstall: 'Uninstall',
  uniqueSerial: 'The serial number must be unique',
  validationError: 'Validation error',
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    DeviceSerialValidatorPipe,
    forwardRef(() => ApplyPipeModule),
    forwardRef(() => CardAccordionComponent),
    forwardRef(() => CustomerItemComponent),
    forwardRef(() => SimpleButtonComponent),
    forwardRef(() => SimpleInputComponent),
    forwardRef(() => TitleContentComponent),
    MatChipsModule,
    MatIconModule,
    TranslateModule,
  ],
  selector: 'customer-virtual-devices-section',
  templateUrl: './virtual-devices-section.component.html',
  styleUrls: ['./virtual-devices-section.component.scss'],
})
export class VirtualDevicesSectionComponent {
  @Input() virtualDevicesConfig!: VirtualDevicesForm;

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() inverterValidators: InverterValidators = {
    syncValidators: [],
    asyncValidators: [],
  };

  @Input() internalManagement = false;

  readonly options: Array<keyof typeof labels> = ['active', 'delete', 'uninstall'];

  readonly selectedSystemDefinition$ = this.store.select(selectedSystem).pipe(isNotNullOrUndefined());

  readonly isFormChanged$ = defer(() => combineLatest([
    this.selectedSystemDefinition$,
    this.virtualDevicesConfig.controls.inverterList.statusChanges,
    this.virtualDevicesConfig.controls.inverterList.valueChanges,
  ])).pipe(
    tap(() => this.changeDetectorRef.detectChanges()),
    map(([system]) => {
      const verifiedDevices = system.metadata?.verifiedDevices || [];
      const verifiedUninstallations = system.metadata?.verifiedUninstallations || [];

      const formInverterDevices = this.virtualDevicesConfig.controls.inverterList.value as {
        inverterSerial: string;
        added: boolean;
        dismiss: RemoveInverterFromPlant | null;
      }[];

      const verifiedDevicesMap = new Map(verifiedDevices.map(d => [d.sn, d]));
      const verifiedUninstallationsMap = new Map(verifiedUninstallations.map(d => [d.sn, d]));
      const formMap = new Map(formInverterDevices.map(inv => [inv.inverterSerial, inv]));

      for (const vd of verifiedDevices) {
        const inv = formMap.get(vd.sn);
        if (!inv || inv.added !== true || inv.dismiss !== null) {
          return true;
        }
      }

      for (const ud of verifiedUninstallations) {
        const inv = formMap.get(ud.sn);
        if (!inv || inv.added !== true || inv.dismiss !== 'Uninstall') {
          return true;
        }
      }

      for (const inv of formInverterDevices) {
        const isVerified = verifiedDevicesMap.has(inv.inverterSerial);
        const isUninstalled = verifiedUninstallationsMap.has(inv.inverterSerial);

        if (!isVerified && !isUninstalled) {
          if (inv.added === true && (inv.dismiss === null || inv.dismiss === 'Uninstall')) {
            return true;
          }
        }
      }

      return false;
    }),
  );

  readonly getDeviceIconIdentifier = getDeviceIconIdentifier;

  get deviceErrorMessage() {
    return getInverterSerialErrorMessage(this.labels, this.genericDeviceValidator);
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly toastFacade: ToastFacade,
    readonly store: Store<SystemsState>,
  ) {}

  removeVirtualInverter(index: number) {
    return () => {
      this.virtualDevicesConfig.controls.inverterList.removeAt(index);
    };
  }

  internalManagementDismiss(index: number, dismiss?: RemoveInverterFromPlant) {
    return () => {
      if (dismiss) {
        const inverterList = this.virtualDevicesConfig.controls.inverterList.value;
        const addedInverters = inverterList.filter(inv => inv.added);
        const allDismiss = addedInverters.filter(inv => inv.dismiss);

        if (addedInverters.length - allDismiss.length > 1) {
          this.virtualDevicesConfig.controls.inverterList.at(index).controls.dismiss?.setValue(dismiss);
        } else {
          this.toastFacade.present(this.labels.atLeastOneActiveInverter, this.labels.attention, { severity: 'info' });
        }
      } else {
        this.virtualDevicesConfig.controls.inverterList.at(index).controls.dismiss?.setValue(null);
      }
    };
  }

  addVirtualInverter(inverter: InverterForm) {
    return () => {
      /**
       * Check if the inverter serial is already added to the list
       */
      const deviceIds = this.virtualDevicesConfig.controls.inverterList.value
        .filter((inv) => inv.added)
        .map((inv) => inv.inverterSerial?.toLowerCase());

      if (deviceIds.includes(inverter.controls.inverterSerial.value?.toLowerCase())) {
        inverter.controls.inverterSerial.setErrors({ duplicate: true });
        return;
      }

      const serial = inverter.controls.inverterSerial.value?.trim();
      if (!serial) {
        inverter.controls.inverterSerial.setErrors({ required: true });
        return;
      }
      inverter.controls.inverterSerial.setValue(serial);

      const currentValue = inverter.controls.added.value;
      inverter.controls.added.setValue(!currentValue);

      this.virtualDevicesConfig.controls.inverterList.push(new FormGroup({
        inverterSerial: new FormControl('',
          this.inverterValidators.syncValidators,
          this.inverterValidators.asyncValidators,
        ),
        added: new FormControl(false),
        dismiss: new FormControl<RemoveInverterFromPlant | null>(null),
      }));
    };
  }
}
